import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import EntityTable from '../components/Listing/EntityTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH, faUserSlash, faArrowUpRightFromSquare, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import PatientImage from '../components/Patient/PatientImage';
import { NavLink } from 'react-router-dom';
import { formatRelativeDate, isToday } from '../utils/FormatDate';
import DateCell from '../components/Listing/DateCell';
import fitColumns from '../components/Listing/fitColumns';
import InputText from '../components/Form/Fields/InputText';
import FabGroup from '../components/FabGroup';
import PatientSmartReturnModal from '../components/Modal/PatientSmartReturnModal';
import { useAuth } from '../context/AuthProvider';
import { API_BASE_URL } from '../api';

const formatAmount = (val) =>
    new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val ?? 0);

const StatusBadge = () => {
    const { t } = useTranslation();
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: '#dcfce7', color: '#16a34a',
            padding: '4px 10px', borderRadius: 999,
            fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
        }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#16a34a', flexShrink: 0 }} />
            {t('returns.status.processed')}
        </span>
    );
};

export default function Returns() {
    const { t } = useTranslation();
    const { setTitle, setTitleIcon } = usePageTitle();
    const { hasRight } = useAuth();
    const canCreateReturn = hasRight('RETURNS_CREATE');
    const tableRef = useRef(null);
    const [showCreateModal, setShowCreateModal] = useState(false);

    useEffect(() => {
        setTitle(`${t('Returns')}<span style="display:block;font-size:11px;font-weight:400;color:#9ca3af;margin-top:5px;">${t('returns.subtitle')}</span>`);
        document.title = t('Returns');
        setTitleIcon('ti-arrow-back-up');
        return () => setTitleIcon(null);
    }, [setTitle, setTitleIcon, t]);

    useBreadcrumbSetup([{ label: t('Returns'), url: '/returns' }]);

    const baseColumns = [
        {
            Header: '#',
            accessor: 'id',
            sortable: true,
            Cell: (row) => <span style={{ fontSize: 14, fontWeight: 500, color: '#9ca3af' }}>{row.id}</span>,
        },
        {
            Header: t('returns.column.reference'),
            accessor: 'reference',
            sortable: true,
            Cell: (row) => (
                <div>
                    {/* same reference style as the orders listing (orderColumns.js) */}
                    <NavLink to={`/returns/${row.id}`} className="cell-name" style={{ whiteSpace: 'nowrap', fontSize: 13.5 }}>{row.reference}</NavLink>
                </div>
            ),
        },
        {
            Header: t('returns.column.amount'),
            accessor: 'total',
            sortable: true,
            className: 'text-end',
            Cell: (row) => (
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 4 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{formatAmount(row.total)}</span>
                    <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>dhs</span>
                </div>
            ),
        },
        {
            // exact date + time, relative age underneath (same as the sales listing)
            Header: t('Date'),
            accessor: 'createdAt',
            Cell: (row) => <DateCell date={row.createdAt} />,
        },
        {
            Header: t('Status'),
            accessor: 'status',
            className: 'text-center',
            Cell: () => <StatusBadge />,
        },
        {
            Header: t('Order'),
            accessor: 'orderReference',
            sortable: true,
            className: 'text-center',
            filterable: { component: InputText },
            Cell: (row) => (
                <NavLink
                    to={`/order/${row.orderId}`}
                    style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, borderRadius: 8,
                        background: '#f3f4f6', color: '#6b7280',
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#2563eb'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#6b7280'; }}
                    data-tooltip-id="tooltip"
                    data-tooltip-content={row.orderReference}
                >
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: 12 }} />
                </NavLink>
            ),
        },
        {
            Header: t('returns.column.items'),
            accessor: 'itemCount',
            className: 'text-center',
            Cell: (row) => (
                <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 28, height: 28, borderRadius: '50%',
                    background: '#f3f4f6', fontSize: 13, fontWeight: 700, color: '#374151',
                }}>
                    {row.itemCount}
                </span>
            ),
        },
        {
            Header: t('Patient'),
            accessor: 'patientName',
            filterable: { component: InputText },
            Cell: (row) => {
                if (!row.patientName) return (
                    <div className="d-flex align-items-center gap-2">
                        <span style={{
                            width: 32, height: 32, borderRadius: '50%',
                            background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <FontAwesomeIcon icon={faUserSlash} style={{ color: '#9ca3af', fontSize: 13 }} />
                        </span>
                        <span className="cell-sub" style={{ marginTop: 0 }}>{t('returns.visitor')}</span>
                    </div>
                );
                const parts = row.patientName.trim().split(' ');
                const patient = { firstName: parts[0] || '', lastName: parts.slice(1).join(' ') || '' };
                return (
                    <div className="d-flex align-items-center gap-2">
                        <PatientImage patient={patient} size={32} />
                        <NavLink to={`/patient/${row.patientId}`} className="cell-name">
                            {patient.firstName}{patient.lastName ? ` ${patient.lastName[0]}.` : ''}
                        </NavLink>
                    </div>
                );
            },
        },
        {
            Header: t('returns.column.reason'),
            accessor: 'reason',
            visible: false, // secondary: in "Colonnes"
            Cell: (row) => row.reason
                ? <span style={{ fontSize: 13, color: '#6b7280', maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.reason}</span>
                : <span className="cell-muted">—</span>,
        },
        {
            Header: '',
            accessor: 'actions',
            className: 'col-actions',
            Cell: (row) => (
                <div className="cell-dropdown">
                    <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                        <FontAwesomeIcon icon={faEllipsisH} />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                        <li>
                            <NavLink to={`/returns/${row.id}`} className="dropdown-item">
                                {t('returns.action.view_return')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink to={`/order/${row.orderId}`} className="dropdown-item">
                                {t('View order')}
                            </NavLink>
                        </li>
                    </ul>
                </div>
            ),
        },
    ];
    // only the reference stretches, the others fit their content
    const columns = fitColumns(baseColumns, 'reference');

    // Card-view design for this listing (Table's enableCardView feature, forced on mobile)
    // — reuses the actions column's own dropdown so both views offer identical actions.
    const actionsColumn = columns.find((c) => c.accessor === 'actions');
    const renderReturnCard = (row) => (
        <div className="card h-100 shadow-sm">
            <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
                <div style={{ alignSelf: 'flex-start' }}><StatusBadge /></div>

                <NavLink to={`/returns/${row.id}`} style={{ fontSize: 13.5, fontWeight: 600, color: '#111827', marginTop: 4 }}>
                    {row.reference}
                </NavLink>
                <span style={{ fontSize: 11.5, color: '#9ca3af' }}>{formatRelativeDate(row.createdAt)}</span>

                {row.patientName ? (
                    <div className="d-flex align-items-center gap-2" style={{ marginTop: 2 }}>
                        <PatientImage patient={{ firstName: row.patientName.split(' ')[0], lastName: row.patientName.split(' ').slice(1).join(' ') }} size={28} />
                        <span style={{ fontSize: 12.5, color: '#374151' }}>{row.patientName}</span>
                    </div>
                ) : (
                    <span style={{ fontSize: 11.5, color: '#9ca3af' }}>{t('returns.visitor')}</span>
                )}

                {row.reason && (
                    <span style={{ fontSize: 11.5, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.reason}
                    </span>
                )}

                <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 'auto', paddingTop: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{formatAmount(row.total)}</span>
                        <span style={{ fontSize: 10.5, color: '#9ca3af', fontWeight: 500 }}>dhs</span>
                    </div>
                    {actionsColumn?.Cell(row)}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <EntityTable
                ref={tableRef}
                endpoint={`${API_BASE_URL}/api/v1/returns`}
                columns={columns}
                // today's rows get a light tint, to spot them at a glance (same as the sales listing)
                rowClassName={(row) => (isToday(row.createdAt) ? 'row-today' : '')}
                defaultSort="id"
                defaultDirection="desc"
                pageSize={20}
                emptyMessage={t('returns.empty')}
                searchable={true}
                searchPlaceholder={t('returns.search_placeholder')}
                multiSelect={true}
                enableCardView={true}
                renderCard={renderReturnCard}
            />

            {canCreateReturn && (
                <FabGroup
                    buttons={[
                        { id: 'create-return', label: t('Create return'), icon: faRotateLeft, onClick: () => setShowCreateModal(true) },
                    ]}
                />
            )}

            <PatientSmartReturnModal
                show={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSave={() => tableRef.current?.reload()}
            />
        </>
    );
}
